
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { SleepFormData } from "../../SleepFormContext";
import { HandleSleepSessionParams } from "../sleep-dialog-types";
import { logTimezoneDebugInfo } from "../utils/submission-utils";

export function useSleepCreateService() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createSleepSession = async (values: SleepFormData) => {
    try {
      // Validate and ensure we have proper Date objects
      const startDate = values.start_time instanceof Date ? values.start_time : new Date(values.start_time);
      const endDate = values.end_time ? (values.end_time instanceof Date ? values.end_time : new Date(values.end_time)) : null;
      
      // Validate that dates are valid
      if (!startDate || isNaN(startDate.getTime())) {
        throw new Error('Invalid start time. Please select a valid date and time.');
      }
      
      if (endDate && isNaN(endDate.getTime())) {
        throw new Error('Invalid end time. Please select a valid date and time.');
      }
      
      // Debug logs
      logTimezoneDebugInfo(startDate, endDate);
      console.log('Child ID being sent:', values.childId);
      console.log('Has woken up status:', values.has_woken_up);
      console.log('Session status:', values.status);
      
      const rpcParams: HandleSleepSessionParams = {
        p_caregiver_id: values.caregiverId,
        p_child_id: values.childId,
        p_start_time: startDate.toISOString(),
        p_end_time: endDate ? endDate.toISOString() : null,
        p_location_id: values.location_id,
        p_environment_id: values.environment_id,
        p_onset_method_id: values.onset_method_id,
        p_restfulness_id: values.restfulness_id || null,
        p_signs_of_sleep_debt: values.signs_of_sleep_debt || false,
        p_sound_id: values.sound_id,
        p_sound_level_id: values.sound_level_id,
        p_notes: values.notes?.trim() || "",
        p_has_woken_up: values.has_woken_up || 'unknown',
        p_status: values.status || 'in_progress'
      };
      
      console.log(' New sleep session RPC params:', rpcParams);

      const { data: result, error } = await supabase.rpc(
        'handle_sleep_session',
        rpcParams
      );

      if (error) throw error;

      if (typeof result === 'string' && result.startsWith('Error:')) {
        throw new Error(result);
      }

      console.log(' Sleep session created successfully:', result);

      queryClient.invalidateQueries({ queryKey: ['sleepSessions'] });
      queryClient.invalidateQueries({ queryKey: ['childSleepRecords', values.childId] });

      if (typeof window.refreshSleepHistory === 'function') {
        console.log(' Refreshing sleep history via window function');
        window.refreshSleepHistory();
      }

      toast({
        title: "Success",
        description: values.status === 'in_progress' 
          ? "Sleep session saved - you can finish it later" 
          : "Sleep session recorded successfully",
        duration: 2000
      });
      
      return true;
    } catch (error) {
      console.error(' Error in sleep session create:', error);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to submit sleep session"
      });
      
      return false;
    }
  };

  return { createSleepSession };
}
